import { StateCreator } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';
import { asyncify, each, eachLimit } from 'async';

import { GameSoundType, Icon, PartKey, TtsLocale } from '../../models/Base';
import { StoreLifecycle, StoreLifecycleAction } from '../../models/Store';
import {
  BasicComponent,
  createGamePart,
  DraftStoreAction,
  DraftStoreState,
  GameDesignStyle,
  GameDraft,
  GamePart,
  GameProduct,
  gameProductConvertFunc,
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
  StationDraft,
  TextDraft,
} from '../../models/DraftStore';
import {
  GameData,
  GameTypes,
  I18nPartInfo,
  TextOrderingSoundMode,
  TextOrderingUnit,
  TextOrderingUnitType,
} from '../../models/GameStore';
import { AlbumAudience, AlbumContentType } from '../../models/SpaceStore';

import {
  CreateGamesInGameCollectionBody,
  createGameCollection as restCreateGameCollection,
  createGamesInGameCollection as restCreateGamesInGameCollection,
} from '../../api/Rest/game';
import { createAlbum as restCreateAlbum } from '../../api/Rest/space';
import { getTtsAudios as restGetTtsAudios } from '../../api/Rest/files';
import {
  translateLanguage as restTranslateLanguage,
  textDetectLanguage as restTextDetectLanguage,
} from '../../api/Rest/language';
import { CREATE_GAME_FILTER_WORDS } from '../../constants/MemoryGames/createGame';
import { WordSelectionMode } from '../../constants/MemoryGames/wordSelectionMode';
import {
  StationEmptyItemNo,
  StationGameItemNo,
  StationGoalItemNo,
  StationItemNo,
  StationMiniChallengeItemNo,
  StationRewardItemNo,
  StationSlideshowItemNo,
  StationStartItemNo,
  StationSuperChallengeItemNo,
  StationTutorGameItemNo,
  StationType,
} from '../../constants/Games/gameStation';
import { stationItemsMap } from '../../constants/Games/gameStationUtils';
import {
  isNotEmptyString,
  selectRandomWords,
  splitIntoChunks,
  splitIntoWords,
} from '../../utils/string';
import { createI18nInfo } from '../../utils/i18n';

type Store = StoreLifecycleAction & DraftStoreState & DraftStoreAction;

const lifecycle = new StoreLifecycle('FileStore');
lifecycle.finishHydration();

const initializer: StateCreator<Store, [], []> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      set({
        managedClassroomId: undefined,
        gameContents: [],
        gameDrafts: [],
        gameProducts: [],
        gameParts: [],
        stationDrafts: [],
        selectWords: [],
        mode: undefined,
        hasShownDividedPopup: false,
        hasAssistedGame: true,
      });
    },
    () => set((state) => ({ ...state })),
  );

  const clear: DraftStoreAction['clear'] = () => {
    set((state) => {
      state.gameContents = [];
      state.gameDrafts = [];
      state.gameProducts = [];
      state.gameParts = [];
      state.stationDrafts = [];
      state.selectWords = [];
      state.mode = undefined;
      state.hasShownDividedPopup = false;
      state.hasAssistedGame = true;
      state.designStyle = createDefaultGameDesignStyle();
      return { ...state };
    });
  };

  const getManagedClassroomId: DraftStoreAction['getManagedClassroomId'] = () =>
    get().managedClassroomId;
  const setManagedClassroomId: DraftStoreAction['setManagedClassroomId'] = (
    classroomId: string,
  ) => {
    set((state) => {
      state.managedClassroomId = classroomId;
      return { ...state };
    });
  };

  const getGameMode: DraftStoreAction['getGameMode'] = () => get().mode;
  const setGameMode: DraftStoreAction['setGameMode'] = (
    mode: QuickCreateGameMode,
  ) => {
    set((state) => {
      state.mode = mode;
      return { ...state };
    });
  };

  const getLocale: DraftStoreAction['getLocale'] = () => get().locale;
  const setLocale: DraftStoreAction['setLocale'] = (locale: TtsLocale) => {
    set((state) => {
      state.locale = locale;
      return { ...state };
    });
  };

  const getHasAssistedGame: DraftStoreAction['getHasAssistedGame'] = () =>
    get().hasAssistedGame;
  const setHasAssistedGame: DraftStoreAction['setHasAssistedGame'] = (
    hasAssistedGame: boolean,
  ) => {
    const stationDrafts = get().stationDrafts;

    const newStationDrafts = stationDrafts.map((station) => {
      if (station.stationType === StationType.AssistedGame) {
        return { ...station, hidden: !hasAssistedGame };
      }
      return station;
    });

    set((state) => {
      state.hasAssistedGame = hasAssistedGame;
      state.stationDrafts = newStationDrafts;
      return { ...state };
    });
  };

  const createDefaultGameDesignStyle = (): GameDesignStyle => {
    const getStationBackgrounds = (num: number) => {
      return Array.from(Array(16)).map((_, index) => index * 3 + num);
    };

    const getRandomItem = <T>(items: T[]): T => {
      if (items.length === 0) {
        throw new Error('The items array must have a length greater than 0');
      }

      const randomIndex = Math.floor(Math.random() * items.length);
      return items[randomIndex]!;
    };

    const getStationItems = (type: string) => {
      const filterStations: StationItemNo[] = [];
      [...stationItemsMap.entries()].forEach(([itemId, item]) => {
        if (item.rewardSource === 'Default' && item.type === type) {
          filterStations.push(itemId);
        }
      });
      return filterStations;
    };

    const entertainment = getStationBackgrounds(0);
    const education = getStationBackgrounds(1);
    const enterprise = getStationBackgrounds(2);
    return {
      stationBackground: { set: getRandomItem(education) },
      stationStyle: {
        start: getRandomItem(
          getStationItems('stationStart'),
        ) as StationStartItemNo,
        goal: getRandomItem(
          getStationItems('stationGoal'),
        ) as StationGoalItemNo,
        empty: getRandomItem(
          getStationItems('stationEmpty'),
        ) as StationEmptyItemNo,
        game: getRandomItem(
          getStationItems('stationGame'),
        ) as StationGameItemNo,
        slideshow: getRandomItem(
          getStationItems('stationSlideshow'),
        ) as StationSlideshowItemNo,
        miniChallenge: getRandomItem(
          getStationItems('stationMiniChallenge'),
        ) as StationMiniChallengeItemNo,
        superChallenge: getRandomItem(
          getStationItems('stationSuperChallenge'),
        ) as StationSuperChallengeItemNo,
        reward: getRandomItem(
          getStationItems('stationReward'),
        ) as StationRewardItemNo,
        assistedGame: getRandomItem(
          getStationItems('stationAssistedGame'),
        ) as StationTutorGameItemNo,
      },
      stationDecoration: {
        set: getRandomItem(Array.from({ length: 6 }, (_, index) => index)),
        images: {},
      },
      thinkingTimeLimit: 'auto',
      backgrounds: {
        entertainment: entertainment.map((num) => ({ set: num })),
        education: education.map((num) => ({ set: num })),
        enterprise: enterprise.map((num) => ({ set: num })),
      },
      themeColor: '#4C6CAE',
    };
  };

  const createGameDraft: DraftStoreAction['createGameDraft'] = (
    gameDrafts: GameDraft[],
  ) => {
    set((state) => {
      state.gameDrafts = gameDrafts;
      return { ...state };
    });
  };
  const getGameDrafts: DraftStoreAction['getGameDrafts'] = () =>
    get().gameDrafts;

  const getGameDraft: DraftStoreAction['getGameDraft'] = (index: number) =>
    get().gameDrafts[index];

  const createNewBasicComponent = (text: string) => {
    const locale = get().locale;
    const mode = get().mode ?? QuickCreateGameMode.QuickMemory;
    const gameModeProps = getQuickCreateGameModeProps(mode);
    const questionComponent: BasicComponent = new BasicComponent(
      locale,
      new TextDraft(
        gameModeProps.defaultQuestionText,
        true,
        1,
        gameModeProps.splitLimitation,
        [],
        true,
        'Enter question or description',
      ),
      // undefined,
      // undefined,
      // undefined,
      true,
      true,
    );
    const answerComponent: BasicComponent = new BasicComponent(
      locale,
      new TextDraft(
        text,
        true,
        1,
        gameModeProps.splitLimitation,
        [],
        true,
        'Enter answer',
      ),
      // undefined,
      // undefined,
      // undefined,
      true,
      true,
    );
    const draft: GameDraft = {
      game: {
        question: questionComponent,
        answer: answerComponent,
      },
      convertTypes: gameModeProps.convertType,
      locale: locale,
    };
    return draft;
  };

  const convertContentsToDrafts: DraftStoreAction['convertContentsToDrafts'] = (
    gameContents: string[],
  ) => {
    const locale = get().locale;
    const mode = get().mode ?? QuickCreateGameMode.QuickMemory;
    const gameDrafts: GameDraft[] = [];
    const gameModeProps = getQuickCreateGameModeProps(mode);
    gameContents.forEach((gameContent) => {
      const plainText = gameContent;

      const splitedChunks = splitIntoChunks(
        plainText,
        locale,
        gameModeProps.splitLimitation,
      );

      splitedChunks.forEach((chunk) => {
        if (isNotEmptyString(chunk)) {
          const draft = createNewBasicComponent(chunk);
          gameDrafts.push(draft);
        }
      });
    });
    set((state) => {
      state.gameContents = gameContents;
      state.gameDrafts = gameDrafts;
      state.hasShownDividedPopup = false;
      return { ...state };
    });
  };

  const scanTextFromImage: DraftStoreAction['scanTextFromImage'] = async (
    image: string,
  ) => {
    const textDetected = await restTextDetectLanguage({
      images: [image],
    });
    if (textDetected.ok) {
      console.log(textDetected.data);
      console.log(textDetected.data.detections);
      return textDetected.data.detections[0]?.[0]?.description;
    }
    return undefined;
  };

  const getGameContents: DraftStoreAction['getGameContents'] = () =>
    get().gameContents;

  const setGameContents: DraftStoreAction['setGameContents'] = (
    gameContents: string[],
  ) => {
    set((state) => {
      state.gameContents = gameContents;
      return { ...state };
    });
  };

  const addGameDraft: DraftStoreAction['addGameDraft'] = () => {
    const draft = createNewBasicComponent('');
    set((state) => {
      state.gameDrafts.push(draft);
      return { ...state };
    });
  };

  const removeGameDraft: DraftStoreAction['removeGameDraft'] = (
    index: number,
  ) => {
    if (get().gameDrafts.length === 1) {
      const draft = createNewBasicComponent('');
      set((state) => {
        state.gameDrafts = [draft];
        return { ...state };
      });
    } else {
      set((state) => {
        state.gameDrafts = state.gameDrafts.filter((_, i) => index !== i);
        return { ...state };
      });
    }
  };

  const editGameDraft: DraftStoreAction['editGameDraft'] = (
    index: number,
    draft: GameDraft,
  ) => {
    set((state) => {
      state.gameDrafts[index] = draft;
      return { ...state };
    });
  };

  const autoTranslateAnswer: DraftStoreAction['autoTranslateAnswer'] = async (
    translateString: string,
    from: TtsLocale,
    to: TtsLocale,
  ) => {
    const translateLanguage = await restTranslateLanguage({
      inputs: [translateString],
      from: from,
      to: to,
    });
    if (translateLanguage.ok) {
      const translations = translateLanguage.data.translations;
      return translations[0]?.output;
    }
    return undefined;
  };

  const isNeedToShowDividedPopup: DraftStoreAction['isNeedToShowDividedPopup'] =
    () => {
      const hasShownDividedPopup = get().hasShownDividedPopup;
      if (hasShownDividedPopup) {
        return false;
      }
      if (get().gameContents.length < get().gameDrafts.length) {
        set((state) => {
          return { ...state, hasShownDividedPopup: true };
        });
        return true;
      }
      return false;
    };

  const setShowDividedPopup: DraftStoreAction['setShowDividedPopup'] = (
    hasShownDividedPopup: boolean,
  ) => {
    set((state) => {
      return { ...state, hasShownDividedPopup: hasShownDividedPopup };
    });
  };

  const convertDraftsToSelectWords: DraftStoreAction['convertDraftsToSelectWords'] =
    () => {
      const drafts = get().gameDrafts;
      const selectWords: TextOrderingUnit[][] = [];
      drafts.forEach((draft) => {
        const plainText = draft.game.answer.text.text;

        const splitWords = splitIntoWords(plainText, draft.locale);
        const wordCount = splitIntoWords(plainText, draft.locale, {
          withSpace: false,
        }).length;
        const wordWeights = splitWords.map((word) => {
          if (
            word.isWordLike &&
            !CREATE_GAME_FILTER_WORDS.includes(word.text.toLowerCase())
          ) {
            return word.text.length;
          }
          return 0;
        });

        const indexes = selectRandomWords(
          splitWords.map((word) => word.text),
          wordWeights,
          Math.max(Math.min(6, wordCount), Math.floor(wordCount * 0.2)),
        );

        const selectWord: TextOrderingUnit[] = splitWords.map((word, index) => {
          return {
            type: indexes.includes(index)
              ? TextOrderingUnitType.Blank
              : TextOrderingUnitType.Hint,
            key: word.text,
          };
        });
        selectWords.push(selectWord);
      });

      set((state) => {
        state.selectWords = selectWords;
        return { ...state };
      });
    };

  const changeSelectWordsUnitType: DraftStoreAction['changeSelectWordsUnitType'] =
    (cellIndex: number, unitIndex: number) => {
      const drafts = get().gameDrafts;
      const selectWords = get().selectWords;
      const draft = drafts[cellIndex];
      const word = selectWords[cellIndex];
      if (word && draft) {
        const unit = word[unitIndex];

        if (unit) {
          const isWordLike = splitIntoWords(unit.key, draft.locale)[0]
            ?.isWordLike;
          const type = unit.type;
          unit.type =
            type === TextOrderingUnitType.Hint && isWordLike
              ? TextOrderingUnitType.Blank
              : TextOrderingUnitType.Hint;
        }
      }
      set((state) => {
        state.selectWords = selectWords;
        return { ...state };
      });
    };

  const changeSelectWordsMode: DraftStoreAction['changeSelectWordsMode'] = (
    cellIndex: number,
    mode: WordSelectionMode,
  ) => {
    const drafts = get().gameDrafts;
    const selectWords = get().selectWords;
    const draft = drafts[cellIndex];
    const selectWordCell = selectWords[cellIndex];
    if (selectWordCell && draft) {
      if (mode === WordSelectionMode.SelectAllWords) {
        const newWords: TextOrderingUnit[] = selectWordCell.map((unit) => {
          const isWordLike = splitIntoWords(unit.key, draft.locale)[0]
            ?.isWordLike;
          return {
            key: unit.key,
            type: isWordLike
              ? TextOrderingUnitType.Blank
              : TextOrderingUnitType.Hint,
          };
        });
        selectWords[cellIndex] = newWords;
      } else if (mode === WordSelectionMode.HandpickKeywordsSuggestions) {
        const plainText = selectWordCell.map((unit) => unit.key).join('');

        const splitWords = splitIntoWords(plainText, draft.locale);
        const wordWeights = splitWords.map((word) => {
          if (
            word.isWordLike &&
            !CREATE_GAME_FILTER_WORDS.includes(word.text.toLowerCase())
          ) {
            return word.text.length;
          }
          return 0;
        });

        const indexes = selectRandomWords(
          splitWords.map((word) => word.text),
          wordWeights,
          Math.max(
            Math.min(6, splitWords.length),
            Math.floor(splitWords.length * 0.2),
          ),
        );

        const selectWord: TextOrderingUnit[] = splitWords.map((word, index) => {
          return {
            type: indexes.includes(index)
              ? TextOrderingUnitType.Blank
              : TextOrderingUnitType.Hint,
            key: word.text,
          };
        });
        selectWords[cellIndex] = selectWord;
      } else {
        const newWords: TextOrderingUnit[] = selectWordCell.map((unit) => {
          return {
            key: unit.key,
            type: TextOrderingUnitType.Hint,
          };
        });
        selectWords[cellIndex] = newWords;
      }
    }
    set((state) => {
      state.selectWords = selectWords;
      return { ...state };
    });
  };

  const getSelectWords: DraftStoreAction['getSelectWords'] = () =>
    get().selectWords;

  const getGameProducts: DraftStoreAction['getGameProducts'] = () =>
    get().gameProducts;

  const createPathStations = () => {
    const gameProducts = get().gameProducts;
    const gameParts = get().gameParts;
    const hasAssistedGame = get().hasAssistedGame;
    const designStyle = get().designStyle;

    const stationDrafts: StationDraft[] = [];

    gameParts.forEach((part) => {
      const partProducts = gameProducts.filter(
        (product) => product.partId === part.partId,
      );

      partProducts.forEach((partProduct) => {
        // default assisted game = 'before
        const assistedStationDraft: StationDraft = {
          stationItem: designStyle.stationStyle.assistedGame,
          stationType: StationType.AssistedGame,
          title: partProduct.title,
          subtitle: partProduct.description,
          isSelected: true,
          hidden: !hasAssistedGame,
          partId: part.partId,
          gameIndex: partProduct.index,
        };
        stationDrafts.push(assistedStationDraft);

        const gameStationDraft: StationDraft = {
          stationItem: designStyle.stationStyle.game,
          stationType: StationType.Game,
          title: partProduct.title,
          subtitle: partProduct.description,
          isSelected: true,
          hidden: false,
          partId: part.partId,
          gameIndex: partProduct.index,
        };
        stationDrafts.push(gameStationDraft);
      });
    });

    set((state) => {
      state.stationDrafts = stationDrafts;
      return { ...state };
    });
  };

  const convertDraftsToProducts: DraftStoreAction['convertDraftsToProducts'] =
    () => {
      const gameDrafts = get().gameDrafts;
      const _selectWords = get().selectWords;
      const gameProducts: GameProduct[] = [];
      const gameParts: GamePart[] = [];

      gameDrafts.forEach((gameDraft, index) => {
        gameDraft.convertTypes.forEach((convertType) => {
          const convertGameProduct = gameProductConvertFunc(convertType);
          const selectWords = _selectWords[index];
          if (selectWords) {
            const tempGamePart = createGamePart(convertType, gameParts.length);
            let gamePart = gameParts.find(
              (part) => part.gameType === tempGamePart.gameType,
            );
            let needToPushGamePart = false;
            if (!gamePart) {
              gamePart = tempGamePart;
              needToPushGamePart = true;
            }

            const product = convertGameProduct(gameDraft, selectWords, {
              draftId: `draft_${index}_convertType`,
              partId: gamePart.partId,
              gameIndex: index,
            });
            if (product) {
              gameProducts.push(product);
              if (needToPushGamePart) {
                gameParts.push(gamePart);
              }
            }
          }
        });
      });

      set((state) => {
        state.gameProducts = gameProducts;
        state.gameParts = gameParts;
        return { ...state };
      });

      createPathStations();
    };

  const getGameParts: DraftStoreAction['getGameParts'] = () => get().gameParts;

  const getStationDrafts: DraftStoreAction['getStationDrafts'] = () =>
    get().stationDrafts;

  const changeSelectedGameType: DraftStoreAction['changeSelectedGameType'] = (
    partId: string,
    checked: boolean,
  ) => {
    const gameProducts = get().gameProducts;
    const gameParts = get().gameParts;
    const stationDrafts = get().stationDrafts;

    const gamePart = gameParts.find((part) => part.partId === partId);
    if (gamePart) {
      const newGameParts = gameParts.map((part) => {
        if (part.partId === gamePart.partId) {
          return { ...part, isSelected: checked };
        }
        return part;
      });

      const newGameProducts = gameProducts.map((product) => {
        if (product.partId === gamePart.partId) {
          return { ...product, isSelected: checked };
        }
        return product;
      });

      const newStationDrafts = stationDrafts.map((station) => {
        if (station.partId === gamePart.partId) {
          return { ...station, isSelected: checked };
        }
        return station;
      });

      set((state) => {
        state.gameParts = newGameParts;
        state.gameProducts = newGameProducts;
        state.stationDrafts = newStationDrafts;
        return { ...state };
      });
    }
  };

  const changeSelectedGame: DraftStoreAction['changeSelectedGame'] = (
    partId: string,
    gameIndex: number,
    checked: boolean,
  ) => {
    const gameProducts = get().gameProducts;
    const gameParts = get().gameParts;
    const stationDrafts = get().stationDrafts;

    const gamePart = gameParts.find((part) => part.partId === partId);

    if (gamePart) {
      const newGameProducts = gameProducts.map((product) => {
        if (product.partId === gamePart.partId && product.index === gameIndex) {
          return { ...product, isSelected: checked };
        }
        return product;
      });

      const newStationDrafts = stationDrafts.map((station) => {
        if (
          station.partId === gamePart.partId &&
          station.gameIndex === gameIndex
        ) {
          return { ...station, isSelected: checked };
        }
        return station;
      });

      const newGameParts = gameParts.map((part) => {
        if (part.partId === gamePart.partId) {
          const isSelected = newStationDrafts
            .filter((station) => station.partId === gamePart.partId)
            .some((station) => station.isSelected);
          return { ...part, isSelected: isSelected };
        }
        return part;
      });

      set((state) => {
        state.gameParts = newGameParts;
        state.gameProducts = newGameProducts;
        state.stationDrafts = newStationDrafts;
        return { ...state };
      });
    }
  };

  const getGameCollectionPartInfo = (
    albumName: string,
    locale: TtsLocale,
    gameParts: GamePart[],
  ) => {
    const gameCollectionParts: Partial<
      Record<
        PartKey,
        {
          i18nPartInfo: I18nPartInfo;
          icon?: Icon;
        }
      >
    > = {};

    gameCollectionParts['root'] = {
      i18nPartInfo: createI18nInfo(locale, {
        name: albumName,
      }),
    };

    gameParts.forEach((part) => {
      if (part.isSelected) {
        const i18nPartInfo: I18nPartInfo = createI18nInfo(locale, {
          name: part.name,
        });
        gameCollectionParts[part.partId as PartKey] = {
          i18nPartInfo: i18nPartInfo,
          icon: { tag: part.iconTag },
        };
      }
    });

    return gameCollectionParts;
  };

  const fetchGameTts = async (gameProducts: GameProduct[]) => {
    const ttsAudiosSet: Record<string, Set<string>> = {};
    const ttsAudioResult: Record<string, Record<string, string>> = {};

    const setlocaleAudio = (locale: TtsLocale, text: string) => {
      if (!ttsAudiosSet[locale]) {
        ttsAudiosSet[locale] = new Set();
      }
      ttsAudiosSet[locale]?.add(text);
    };

    const setResultlocaleAudio = (
      locale: TtsLocale,
      text: string,
      fileId: string,
    ) => {
      if (!ttsAudioResult[locale]) {
        ttsAudioResult[locale] = {};
      }
      const tempResult = ttsAudioResult[locale];
      if (tempResult) {
        tempResult[text] = fileId;
      }
    };

    // TODO: make the handWriting
    gameProducts.forEach((product) => {
      Object.values(product.game.i18nGameData.resources).forEach(
        (resource: GameData<GameTypes['textOrdering']['variant']>) => {
          resource.variants.forEach((variant) => {
            if (variant.instruction.sound) {
              if (variant.instruction.sound.type === GameSoundType.Tts) {
                setlocaleAudio(
                  variant.instruction.sound.locale,
                  variant.instruction.sound.text,
                );
              }
            }

            if (variant.answer.soundMode === TextOrderingSoundMode.Normal) {
              variant.answer.words.forEach((word) => {
                setlocaleAudio(variant.answer.locale, word.word);

                // word.units.forEach((unit) => {
                //   setlocaleAudio(variant.answer.locale, unit.key);
                // });
              });
            }
          });
        },
      );
    });

    for (let i = 0; i < 5; i++) {
      const ttsAudiosLocaleString: Record<string, string[]> = {};

      Object.entries(ttsAudiosSet).forEach(([key, value]) => {
        ttsAudiosLocaleString[key] = Array.from(value);
      });

      if (
        Object.values(ttsAudiosLocaleString).reduce(
          (acc, cur) => acc + cur.length,
          0,
        ) === 0
      ) {
        // early return
        continue;
      }

      await each(
        Object.entries(ttsAudiosLocaleString),
        asyncify(
          async ([locale, ttsAudiosString]: [
            locale: TtsLocale,
            ttsAudiosString: string[],
          ]) => {
            for (let j = 0; j < ttsAudiosString.length; j += 10) {
              const itemsToProcess = ttsAudiosString.slice(j, j + 10);
              const getTtsAudios = await restGetTtsAudios({
                texts: itemsToProcess,
                locale: locale,
              });

              if (getTtsAudios.ok) {
                const ttsAudios = getTtsAudios.data.ttsAudios;

                ttsAudios.forEach((item) => {
                  if (item !== null) {
                    setResultlocaleAudio(locale, item.text, item.fileId);

                    ttsAudiosSet[locale]?.delete(item.text);
                  }
                });
              }
            }
          },
        ),
      );
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, i) * 1000),
      );
    }

    // if (
    //   Object.values(ttsAudiosSet).reduce((acc, cur) => acc + cur.size, 0) > 0
    // ) {
    //   console.error(
    //     'Error: Some items are still remaining in the set after 5 attempts.',
    //   );
    // }

    gameProducts.forEach((product) => {
      Object.values(product.game.i18nGameData.resources).forEach(
        (resource: GameData<GameTypes['textOrdering']['variant']>) => {
          resource.variants.forEach((variant) => {
            if (variant.instruction.sound) {
              if (variant.instruction.sound.type === GameSoundType.Tts)
                variant.instruction.sound = {
                  type: GameSoundType.Tts,
                  text: variant.instruction.sound.text,
                  locale: variant.instruction.sound.locale,
                  fileId:
                    ttsAudioResult[variant.instruction.sound.locale]?.[
                      variant.instruction.sound.text
                    ],
                };
            }

            if (variant.answer.soundMode === TextOrderingSoundMode.Normal) {
              variant.answer.words.forEach((word) => {
                word.ttsAudio =
                  ttsAudioResult[variant.answer.locale]?.[word.word];

                // word.units.forEach((unit) => {
                //   unit.ttsAudio = ttsAudioResult[variant.answer.locale]?.[unit.key];
                // });
              });
            }
          });
        },
      );
    });

    return gameProducts;
  };

  const getCreateGamesBatch = (
    gameParts: GamePart[],
    gameProducts: GameProduct[],
    hasAssistedGame: boolean,
  ) => {
    const batches: CreateGamesInGameCollectionBody[] = [];
    let batchCount = 0;
    let gameCount = 0;
    gameParts.forEach((part) => {
      if (part.isSelected) {
        let indexKey = 0;
        gameProducts.forEach((product) => {
          if (product.isSelected && product.partId === part.partId) {
            if (!batches[batchCount]) {
              batches.push({ games: {} });
            }

            const games = batches[batchCount]?.games;

            if (games) {
              // Check if the key already exists in batches[batchCount].games
              if (games[String(product.partId).padStart(2, '0') as PartKey]) {
                // Merge the new item with the existing item using Object.assign
                games[String(product.partId).padStart(2, '0') as PartKey] = {
                  ...games[String(product.partId).padStart(2, '0') as PartKey],
                  [String(indexKey).padStart(3, '0')]: {
                    i18nGameData: product.game.i18nGameData,
                    gameType: product.game.gameType,
                    assistedGame: hasAssistedGame ? 'before' : 'off',
                  },
                };
              } else {
                // Add the new item if the key doesn't exist
                games[String(product.partId).padStart(2, '0') as PartKey] = {
                  [String(indexKey).padStart(3, '0')]: {
                    i18nGameData: product.game.i18nGameData,
                    gameType: product.game.gameType,
                    assistedGame: hasAssistedGame ? 'before' : 'off',
                  },
                };
              }
            }

            gameCount = gameCount + 1;
            indexKey = indexKey + 1;
          }

          if (gameCount >= 10) {
            batchCount = batchCount + 1;
            gameCount = 0;
          }
        });
      }
    });

    return batches;
  };

  const getAlbumContent = (
    gameParts: GamePart[],
    stationDrafts: StationDraft[],
    gameCollectionId: string,
    designStyle: GameDesignStyle,
  ) => {
    const partIcons: Partial<Record<PartKey, string | null>> = {};
    gameParts.forEach((part) => {
      if (part.isSelected) {
        partIcons[String(part.partId).padStart(2, '0') as PartKey] =
          part.iconTag;
      }
    });

    const paths: { type: StationType }[] = [];
    paths.push({ type: StationType.Start });

    const totalGameCount = stationDrafts.filter(
      (station) =>
        station.stationType === StationType.Game && station.isSelected,
    ).length;
    let miniChallengeCount = Math.floor((totalGameCount - 3) / 3);
    let currentGameCount = 0;

    stationDrafts.forEach((station) => {
      if (station.isSelected && !station.hidden) {
        paths.push({ type: station.stationType });
        if (station.stationType === StationType.Game) {
          currentGameCount = currentGameCount + 1;
          if (miniChallengeCount > 0 && currentGameCount >= 3) {
            paths.push({ type: StationType.PathChallenge });
            currentGameCount = 0;
            miniChallengeCount = miniChallengeCount - 1;
          }
        }
      }
    });

    paths.push({ type: StationType.Reward });
    paths.push({ type: StationType.Goal });

    const albumContent = {
      type: AlbumContentType.Both,
      gameCollectionIds: [gameCollectionId],
      partChallenges: {},
      miniChallenges: {},
      partIcons: partIcons,
      path: [
        {
          stations: paths,
          style: {
            background: designStyle.stationBackground,
            stations: designStyle.stationStyle,
            decorations: designStyle.stationDecoration,
          },
        },
      ],
      gameDesign: {
        thinkingTimeLimit: designStyle.thinkingTimeLimit,
        backgrounds: designStyle.backgrounds,
        themeColor: designStyle.themeColor,
      },
    };

    return albumContent;
  };

  const createGames: DraftStoreAction['createGames'] = async (
    albumName: string,
    useMemoryBoost: boolean,
    period: number,
  ) => {
    const managedClassroomId = get().managedClassroomId;
    const locale = get().locale;
    const gameParts = get().gameParts;
    const gameProducts = get().gameProducts;
    const stationDrafts = get().stationDrafts;
    const hasAssistedGame = get().hasAssistedGame;
    const designStyle = get().designStyle;

    if (managedClassroomId) {
      const gameCollectionParts = getGameCollectionPartInfo(
        albumName,
        locale,
        gameParts,
      );

      const createGameCollection = await restCreateGameCollection({
        classroomId: managedClassroomId,
        parts: gameCollectionParts,
      });

      if (createGameCollection.ok) {
        const gameCollection = createGameCollection.data.gameCollection;

        const finalGameProducts = await fetchGameTts(gameProducts);

        const batches = getCreateGamesBatch(
          gameParts,
          finalGameProducts,
          hasAssistedGame,
        );

        await eachLimit(
          batches,
          10,
          asyncify(
            async (batch: CreateGamesInGameCollectionBody): Promise<void> => {
              await restCreateGamesInGameCollection(
                { gameCollectionId: gameCollection.gameCollectionId },
                batch,
              );
            },
          ),
        );

        const albumContent = getAlbumContent(
          gameParts,
          stationDrafts,
          gameCollection.gameCollectionId,
          designStyle,
        );

        const createAlbum = await restCreateAlbum(
          { classroomId: managedClassroomId },
          {
            i18nGameSetInfo: createI18nInfo(locale, {
              name: albumName,
              description: 'description',
              previewText: 'previewText',
            }),
            startedAt: new Date().valueOf(),
            partitionId: null,
            index: undefined,
            icons: [],
            tags: [],
            audience: AlbumAudience.Public,
            isCopyable: true,
            content: albumContent,
            challenge: null,
            // defaultGameDesign: {},
            revision: useMemoryBoost ? { period: period } : null,
          },
        );

        if (createAlbum.ok) {
          clear();
          return {
            classroomId: managedClassroomId,
            gameSetId: createAlbum.data.gameSet.gameSetId,
          };
        }
      }
    }
    return undefined;
  };

  return {
    initialize,
    shutdown,
    managedClassroomId: undefined,
    gameContents: [],
    gameDrafts: [],
    gameProducts: [],
    gameParts: [],
    stationDrafts: [],
    selectWords: [],
    mode: undefined,
    locale: TtsLocale.US,
    hasShownDividedPopup: false,
    hasAssistedGame: true,
    designStyle: createDefaultGameDesignStyle(),
    clear,
    getManagedClassroomId,
    setManagedClassroomId,
    getGameMode,
    setGameMode,
    getLocale,
    setLocale,
    getHasAssistedGame,
    setHasAssistedGame,
    createGameDraft,
    getGameDrafts,
    getGameDraft,
    convertContentsToDrafts,
    scanTextFromImage,
    getGameContents,
    setGameContents,
    addGameDraft,
    removeGameDraft,
    editGameDraft,
    autoTranslateAnswer,
    isNeedToShowDividedPopup,
    setShowDividedPopup,
    getSelectWords,
    convertDraftsToSelectWords,
    changeSelectWordsMode,
    changeSelectWordsUnitType,
    getGameProducts,
    convertDraftsToProducts,
    getGameParts,
    getStationDrafts,
    changeSelectedGameType,
    changeSelectedGame,
    createGames,
  };
};

export const useDraftStore = createWithEqualityFn<Store>()(
  initializer,
  Object.is,
);
